import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { listImageHistory, getPublicHistoryEnabled, togglePublicVisibility } from '../../services/historyApi';
import AppFooter from '../AppFooter';
import { FaArrowLeft, FaHome, FaEye, FaEyeSlash } from 'react-icons/fa';

export default function EventHistory({ pageSize = 100 }) {
  const { tenant } = useParams();
  const tenantNum = Number(tenant) || null;

  const [items, setItems] = useState([]);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [publicHistoryEnabled, setPublicHistoryEnabled] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const navigate = useNavigate();

  const fetchPage = async (reset = false) => {
    if (!tenantNum) return;
    setLoading(true);
    setErr(null);
    try {
      const res = await listImageHistory({
        tenant: tenantNum,
        limit: pageSize,
        offset: reset ? 0 : offset,
      });

      setItems(prev => (reset ? res.items : [...prev, ...res.items]));
      setOffset(prev => (reset ? res.items.length : prev + res.items.length));
    } catch (e) {
      setErr(e.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  // Fetch tenant's public_event_history_enabled setting
  useEffect(() => {
    if (!tenantNum) return;
    getPublicHistoryEnabled(tenantNum)
      .then(res => setPublicHistoryEnabled(res.enabled))
      .catch(() => setPublicHistoryEnabled(false));
  }, [tenantNum]);

  useEffect(() => {
    setItems([]);
    setOffset(0);
    if (tenantNum) fetchPage(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantNum]);

  const groups = useMemo(() => {
    const by = {};
    for (const it of items) {
      const d = new Date(it.createdOn);
      if (!Number.isNaN(d.getTime())) {
        const key = d.toISOString().slice(0, 10);
        (by[key] ||= []).push(it);
      }
    }
    return Object.entries(by).sort(([a], [b]) => (a < b ? 1 : -1));
  }, [items]);

  const handleTogglePublic = useCallback(async (it) => {
    if (!tenantNum || !it.eventItemId) return;
    const newShow = !it.showInPublicHistory;
    setTogglingId(it.fileId);
    try {
      await togglePublicVisibility(tenantNum, it.eventItemId, newShow);
      setItems(prev => prev.map(item =>
        item.fileId === it.fileId
          ? { ...item, showInPublicHistory: newShow }
          : item
      ));
    } catch (e) {
      console.error('Failed to toggle public visibility', e);
    } finally {
      setTogglingId(null);
    }
  }, [tenantNum]);

  const handleDashboard = () => {
    navigate(`/${tenant}/dashboard`);
  };

  return (
    <div className="container-fluid">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h3 className="m-0">Event History</h3>
      </div>

      {err && <div className="alert alert-danger">{err}</div>}

      {groups.map(([date, arr]) => (
        <div key={date} className="mb-4">
          <h5 className="mb-3">{date}</h5>
          <div className="row g-3" style={{ alignItems: 'stretch' }}>
            {arr.map((it) => (
              <div key={`${it.kind}-${it.fileId}`} className="col-12 col-sm-6 col-md-4 col-lg-3">
                <div className="card h-100 shadow-sm">
                  <div className="ratio ratio-4x3 bg-dark d-flex align-items-center justify-content-center">
                    <img
                      src={it.url}
                      alt={it.title || 'image'}
                      className="img-fluid"
                      loading="lazy"
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', objectPosition: 'center' }}
                    />
                  </div>
                  <div className="card-body py-2">
                    <div className="small text-muted">
                      {it.kind === 'item' ? `Event Item #${it.eventItemId}` : `Event #${it.eventId ?? ''}`}
                    </div>
                    <div className="fw-semibold text-truncate">{it.title}</div>
                    {/* Public visibility toggle - only for event items when tenant has public history enabled */}
                    {publicHistoryEnabled && it.kind === 'item' && (
                      <button
                        type="button"
                        className={`btn btn-sm mt-1 ${it.showInPublicHistory ? 'btn-outline-success' : 'btn-outline-secondary'}`}
                        onClick={() => handleTogglePublic(it)}
                        disabled={togglingId === it.fileId}
                        title={it.showInPublicHistory ? 'Visible in public history' : 'Hidden from public history'}
                      >
                        {it.showInPublicHistory ? <FaEye className="me-1" /> : <FaEyeSlash className="me-1" />}
                        {it.showInPublicHistory ? 'Public' : 'Hidden'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <AppFooter
        buttons={[
          {
            icon: <FaArrowLeft />,
            label: "Back",
            onClick: () => navigate(-1),
          },
          {
            icon: <FaHome />,
            label: 'Home',
            onClick: handleDashboard,
          },
        ]}
      />
      {!loading && items.length === 0 && <div className="text-muted">No images yet.</div>}
      {loading && <div className="text-muted">Loading…</div>}
    </div>
  );
}
